import { useQuery } from '@tanstack/react-query';

import { useAuth } from './use-auth';

import { DB_TABLES } from '@/constants/database-tables';
import { supabase } from '@/integrations/supabase/client';

// Application plan vocabulary (v3 — locked 2026-05-01 evening).
// DB CHECK constraint mirrors this set: ('free', 'pro', 'pro_max').
// See tasks/2026-05-01-pricing-redesign-plan.md.
//
// Both paid tiers serve solo owners. Pro Max is "more horsepower" — multi-brand
// voice, more video gen, premium image models — NOT a team / agency product.
export type PlanTier = 'free' | 'pro' | 'pro_max';

export type SubscriptionStatus =
  'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'trialing' | 'paused';

export interface SubscriptionData {
  plan: PlanTier;
  status: SubscriptionStatus | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  /**
   * True if the current logged-in user is the workspace owner who pays for the plan.
   * False if they're a member inheriting the plan from their account owner.
   * Drives BillingSettings CTAs: owners see "Manage billing", members see a read-only
   * "Your workspace owner manages billing" notice.
   */
  isOwner: boolean;
  /**
   * True for users grandfathered onto the legacy $40/$32 Pro pricing.
   * Detected by webhook via the legacy Stripe price ID.
   */
  isLegacyPricing?: boolean;
  /**
   * Which billing system owns this plan. Merchants who install from the Shopify
   * App Store must be billed through Shopify (App Store req 1.2.1 — off-platform
   * billing is grounds for rejection), so they upgrade and cancel on Shopify's
   * hosted plan page, not in Stripe. Direct web signups stay on Stripe.
   */
  billingSource: BillingSource;
  /** The shop this plan is billed against, when billingSource is 'shopify'. */
  shopifyShopDomain: string | null;
}

/** Where a subscription is billed. Mirrors user_subscriptions.billing_source. */
export type BillingSource = 'stripe' | 'shopify';

/** Any paid tier — use as the default gate for non-free features. */
export function isPaid(s: Pick<SubscriptionData, 'plan'> | null | undefined): boolean {
  return s?.plan === 'pro' || s?.plan === 'pro_max';
}

/** Pro tier only (the $19 working tier) — for "Pro and above" gates use isPaid(). */
export function isPro(s: Pick<SubscriptionData, 'plan'> | null | undefined): boolean {
  return s?.plan === 'pro';
}

/** Pro Max only — for premium-tier-exclusive features (multi-brand voice, premium image, more video). */
export function isProMax(s: Pick<SubscriptionData, 'plan'> | null | undefined): boolean {
  return s?.plan === 'pro_max';
}

/**
 * Returns the user-facing display name for a tier. Display names v5
 * (2026-08-11, `src/lib/pricing.ts` is the source of truth): 'pro' = "Core",
 * 'pro_max' = "Pro" — tier keys stayed the same, only display names moved.
 * Not importing getPlan() here to avoid a circular dep (pricing.ts type-imports
 * PlanTier from this file).
 */
export function planDisplayName(plan: PlanTier): string {
  if (plan === 'pro_max') return 'Pro';
  if (plan === 'pro') return 'Core';
  return 'Free';
}

export function useSubscription() {
  const { user } = useAuth();

  return useQuery<SubscriptionData>({
    queryKey: ['subscription', user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<SubscriptionData> => {
      if (!user?.id) {
        return {
          plan: 'free',
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          stripeCustomerId: null,
          isOwner: true,
          billingSource: 'stripe',
          shopifyShopDomain: null,
        };
      }

      // Tenant billing inheritance (2026-05-01): drop the auth.uid()-scoped filter and
      // let RLS resolve the right row via current_account_owner_id(). For solo users
      // this returns their own subscription. For members of a shared account, RLS returns
      // the workspace OWNER's subscription row, so members inherit the owner's plan
      // (Salesforce / Notion / Linear pattern). See:
      //   supabase/migrations/20260501153300_tenant_billing_inheritance.sql
      const { data, error } = await supabase
        .from(DB_TABLES.USER_SUBSCRIPTIONS)
        .select(
          'user_id, status, current_period_end, cancelled_at, stripe_customer_id, plan_name, plan_tier, billing_source, shopify_shop_domain',
        )
        .maybeSingle();

      if (error || !data) {
        return {
          plan: 'free',
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          stripeCustomerId: null,
          isOwner: true,
          billingSource: 'stripe',
          shopifyShopDomain: null,
        };
      }

      // RLS returns the row keyed to current_account_owner_id(). If that row's user_id
      // matches the logged-in user, they're the owner/payer. Otherwise they're a member
      // inheriting the workspace plan.
      const isOwner = data.user_id === user.id;

      const isActiveSub =
        data.status === 'active' || data.status === 'trialing' || data.status === 'past_due';

      const rawTier = (data.plan_tier as PlanTier | null) ?? null;
      const planNameLower = (data.plan_name ?? '').toLowerCase();

      // Plan resolution priority:
      //   1) typed plan_tier column ('pro_max' | 'pro' | 'free')
      //   2) plan_name string match for legacy / migration windows ("pro max" / "max" / "pro")
      //   3) any active sub of unknown shape collapses to 'pro' (paid is paid)
      let plan: PlanTier = 'free';
      if (isActiveSub) {
        if (
          rawTier === 'pro_max' ||
          planNameLower.includes('pro max') ||
          planNameLower.includes('max')
        ) {
          plan = 'pro_max';
        } else if (rawTier === 'free') {
          plan = 'free';
        } else {
          plan = 'pro';
        }
      }

      return {
        plan,
        status: data.status as SubscriptionStatus,
        currentPeriodEnd: data.current_period_end,
        cancelAtPeriodEnd: !!data.cancelled_at,
        stripeCustomerId: data.stripe_customer_id,
        isOwner,
        billingSource: (data.billing_source as BillingSource | null) ?? 'stripe',
        shopifyShopDomain: data.shopify_shop_domain ?? null,
      };
    },
  });
}

/** Shopify hosts the plan-selection page; the app never renders its own for these merchants. */
export function shopifyPlanSelectionUrl(shopDomain: string, appHandle = 'mora-marketer'): string {
  const storeHandle = shopDomain.replace(/\.myshopify\.com$/i, '');
  return `https://admin.shopify.com/store/${storeHandle}/charges/${appHandle}/pricing_plans`;
}

/**
 * The shop a user must be billed through Shopify for, or null if they're a
 * direct (Stripe) customer.
 *
 * Checking the subscription row alone isn't enough: a merchant who just
 * installed from the App Store has no subscription yet, and the row defaults to
 * billing_source='stripe' — which would wrongly send them to Stripe Checkout,
 * the exact thing App Store req 1.2.1 forbids. So fall back to the shop stamped
 * onto their account by the install (shopify-oauth-callback writes
 * app_metadata.shopify_domain when it provisions the merchant).
 */
export function useShopifyBilledShop(): string | null {
  const { user } = useAuth();
  const { data: subscription } = useSubscription();

  if (subscription?.billingSource === 'shopify' && subscription.shopifyShopDomain) {
    return subscription.shopifyShopDomain;
  }
  const installedShop = (user?.app_metadata as { shopify_domain?: unknown } | undefined)
    ?.shopify_domain;
  return typeof installedShop === 'string' && installedShop.length > 0 ? installedShop : null;
}
